import React, { useEffect, useState } from 'react';
import HeroImg from './HeroImg';
import { Governorate, governorates } from '../Data/governorates';
import { cities, City } from '../Data/cites';
import { Eye, EyeOff } from 'lucide-react';
import { MdArrowBackIosNew } from "react-icons/md";
import { useNavigate } from 'react-router-dom';
import { useFormik } from "formik";
import * as Yup from "yup";
import { Trainee } from '../types/user';
import usePersonStore from '../context/RegisterContext';
import toast, { Toaster } from 'react-hot-toast';
import { Config } from '../API/config';
import axios from 'axios';
import BeatLoader from 'react-spinners/esm/BeatLoader';
import useTraineeStore from '../context/TrainerContexts';

const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const CommonRegister = ({ role }: { role: string }) => {
    const [viewPassword, setViewPassword] = useState(false);
    const [selectedGovernorate, setGovernorate] = useState<Governorate>(governorates[0]);
    const [selectedCity, setCity] = useState<City | null>(null);
    const [filteredCities, setFilteredCities] = useState<City[]>(cities.filter(city => city.governorate_id === governorates[0].id));
    const [loading, setLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const thisTrainee = useTraineeStore((state) => state.getTrainee);
    const updateTrainee = useTraineeStore((state) => state.setTrainee);
    const image = usePersonStore((state) => state.getImg);

    enum Sex {
        Male = "male",
        Female = "female"
    }

    const validationSchema = Yup.object({
        name: Yup.string().min(3, "name must be more than 3 letters").max(150).required("name is required"),
        email: Yup.string().matches(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/, "please enter valid email").required("Email is required"),
        phone: Yup.string().matches(phoneRegExp, "Phone number is not valid").required("phone is required"),
        gender: Yup.mixed<Sex>().oneOf(Object.values(Sex)).required("sex is required"),
        governorate: Yup.string().required("governorate is required"),
        city: Yup.string().required("city is required"),
        password: Yup.string().matches(/^.{8,}$/, "must be at least 8 characters.").required("password is required"),
    });

    const handleGovernorateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedGov = governorates.find(gov => gov.governorate_name_ar === e.target.value) || governorates[0];
        setGovernorate(selectedGov);
        const newFilteredCities = cities.filter(city => city.governorate_id === selectedGov.id);
        setFilteredCities(newFilteredCities);
        setCity(newFilteredCities[0] || null); // Reset city to first available option
        formik.setFieldValue("governorate", selectedGov.governorate_name_ar);
        formik.setFieldValue("city", newFilteredCities[0]?.city_name_ar || ""); // Sync with Formik
    };

    const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedCity = filteredCities.find(city => city.city_name_ar === e.target.value) || null;
        setCity(selectedCity);
        formik.setFieldValue("city", selectedCity?.city_name_ar || ""); // Sync with Formik
    };

    const handleViewPass = () => {
        setViewPassword(!viewPassword);
    };

    async function sendData(values: Trainee) {
        try {
            setLoading(true);
            const formData = new FormData();
            if (values.profileImage) {
                formData.append("name", values.name);
                formData.append("email", values.email);
                formData.append("phone", values.phone);
                formData.append("gender", values.gender);
                formData.append("governorate", values.governorate);
                formData.append("city", values.city);
                formData.append("password", values.password);
                formData.append("profileImage", values.profileImage);
            }

            const payload = values.profileImage ? formData : values;
            const headers = values.profileImage ? {} : { "Content-Type": "application/json" };
            const { data } = await axios.post(Config.TraineeRegister, payload, { withCredentials: true, headers });

            if (data.statusCode === 201) {
                toast.success("تم تسجيل طلبك");
                navigate('/home');
            }
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                toast.error(error.response.data.message);
            } else {
                toast.error("خطأ غير متوقع، حاول لاحقًا");
            }
        } finally {
            setLoading(false);
        }
    }

    async function handleSubmit(values: Trainee) {
        if (role === "trainee") {
            await sendData(values);
        } else {
            updateTrainee(values);
            navigate("/register/trainerregister");
        }
    }

    const formik = useFormik({
        initialValues: {
            name: thisTrainee().name || "",
            email: thisTrainee().email || "",
            password: thisTrainee().password || "",
            phone: thisTrainee().phone || "",
            gender: thisTrainee().gender || "male",
            governorate: thisTrainee().governorate || governorates[0].governorate_name_ar,
            city: thisTrainee().city || (filteredCities[0]?.city_name_ar || ""),
            profileImage: image() || null,
        },
        validationSchema,
        onSubmit: handleSubmit,
    });

    // Sync filtered cities when governorate changes
    useEffect(() => {
        const newFilteredCities = cities.filter(city => city.governorate_id === selectedGovernorate.id);
        setFilteredCities(newFilteredCities);
        if (!newFilteredCities.some(city => city.city_name_ar === formik.values.city)) {
            setCity(newFilteredCities[0] || null);
            formik.setFieldValue("city", newFilteredCities[0]?.city_name_ar || "");
        }
    }, [selectedGovernorate]);

    return (
        <>
            <Toaster />
            <div className="flex flex-row min-h-screen w-full">
                <HeroImg />
                <div className="flex flex-col items-end justify-start w-full mt-10 px-24 relative">
                    <button onClick={() => navigate(`/userimage/${role}`)}>
                        <div className='w-[40px] h-[40px] border absolute top-0 left-0 cursor-pointer ml-6 border-neutral-800 rounded-full flex justify-center items-center hover:bg-primary-100 transition-all duration-150 group'>
                            <MdArrowBackIosNew size={22} color='#4a4a4a' className="text-neutral-900 group-hover:translate-x-[-2px] transition-transform duration-150" />
                        </div>
                    </button>
                    <div>
                        <div className="w-full flex justify-end">
                            <h2 className="text-[32px] leading-[67.58px] font-[500] text-neutral-900 flex flex-col md:flex-row-reverse items-end">مرحباً بكم في <div className='mr-2'>Otlob Coach</div></h2>
                        </div>
                        <div className="w-full flex justify-end">
                            <p className="text-[16px] leading-[24px] font-[400] text-neutral-900">هل لديك حساب بالفعل؟<span className="underline mr-1"><a href="/login">تسجيل الدخول</a></span></p>
                        </div>
                    </div>
                    <div className='w-full my-3'>
                        <form onSubmit={formik.handleSubmit}>
                            <div className='w-full my-5'>
                                <label htmlFor="email" className="block mb-2 text-sm font-medium text-neutral-600 text-right">البريد الالكتروني</label>
                                <input
                                    type='email'
                                    id="email"
                                    className="text-right border border-gray-300 text-black text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-4"
                                    required
                                    onChange={formik.handleChange}
                                    value={formik.values.email}
                                    onBlur={formik.handleBlur}
                                />
                                {formik.errors.email && formik.touched.email ? <div className="text-red-500">{formik.errors.email}</div> : null}
                            </div>
                            <div className="grid md:grid-cols-3 gap-8">
                                <div className='md:order-3'>
                                    <div className='w-full my-5'>
                                        <label htmlFor="name" className="block mb-2 text-sm font-medium text-neutral-600 text-right">الاسم</label>
                                        <input
                                            type='text'
                                            id="name"
                                            className="text-right border border-gray-300 text-black text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-4"
                                            required
                                            onChange={formik.handleChange}
                                            value={formik.values.name}
                                            onBlur={formik.handleBlur}
                                        />
                                        {formik.errors.name && formik.touched.name ? <div className="text-red-500">{formik.errors.name}</div> : null}
                                    </div>
                                </div>
                                <div className='md:order-2'>
                                    <div className='w-full my-5'>
                                        <label htmlFor="phone" className="block mb-2 text-sm font-medium text-neutral-600 text-right">رقم الهاتف</label>
                                        <input
                                            type='tel'
                                            id="phone"
                                            className="text-right border border-gray-300 text-black text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-4"
                                            required
                                            onChange={formik.handleChange}
                                            value={formik.values.phone}
                                            onBlur={formik.handleBlur}
                                        />
                                        {formik.errors.phone && formik.touched.phone ? <div className="text-red-500">{formik.errors.phone}</div> : null}
                                    </div>
                                </div>
                                <div className='md:order-1'>
                                    <div className='w-full my-5'>
                                        <label htmlFor="gender" className="block mb-2 text-sm font-medium text-neutral-600 text-right">النوع</label>
                                        <select
                                            id="gender"
                                            onChange={formik.handleChange}
                                            value={formik.values.gender}
                                            onBlur={formik.handleBlur}
                                            className="text-right border border-gray-300 text-black text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-3"
                                        >
                                            <option value="male">ذكر</option>
                                            <option value="female">انثي</option>
                                        </select>
                                        {formik.errors.gender && formik.touched.gender ? <div className="text-red-500">{formik.errors.gender}</div> : null}
                                    </div>
                                </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className='md:order-2'>
                                    <div className='w-full my-5'>
                                        <label htmlFor="governorate" className="block mb-2 text-sm font-medium text-neutral-600 text-right">المحافظة</label>
                                        <select
                                            id="governorate"
                                            onChange={handleGovernorateChange}
                                            value={formik.values.governorate}
                                            onBlur={formik.handleBlur}
                                            className="text-right border border-gray-300 text-black text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-3"
                                        >
                                            {governorates.map((governorate, index) => (
                                                <option key={index} value={governorate.governorate_name_ar}>{governorate.governorate_name_ar}</option>
                                            ))}
                                        </select>
                                        {formik.errors.governorate && formik.touched.governorate ? <div className="text-red-500">{formik.errors.governorate}</div> : null}
                                    </div>
                                </div>
                                <div className='md:order-1'>
                                    <div className='w-full my-5'>
                                        <label htmlFor="city" className="block mb-2 text-sm font-medium text-neutral-600 text-right">المنطقة</label>
                                        <select
                                            id="city"
                                            onChange={handleCityChange}
                                            value={formik.values.city}
                                            onBlur={formik.handleBlur}
                                            className="text-right border border-gray-300 text-black text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-3"
                                        >
                                            {filteredCities.length > 0 ? (
                                                filteredCities.map((city, index) => (
                                                    <option key={index} value={city.city_name_ar}>{city.city_name_ar}</option>
                                                ))
                                            ) : (
                                                <option value="">اختر المحافظة أولاً</option>
                                            )}
                                        </select>
                                        {formik.errors.city && formik.touched.city ? <div className="text-red-500">{formik.errors.city}</div> : null}
                                    </div>
                                </div>
                            </div>
                            <div className='w-full my-5'>
                                <label htmlFor="password" className="block mb-2 text-sm font-medium text-neutral-600 text-right">كلمة السر</label>
                                <div className="flex text-right border border-gray-300 text-neutral-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 w-full p-4">
                                    <input
                                        value={formik.values.password}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        className="bg-transparent focus:outline-none w-full text-right order-2 text-black"
                                        type={viewPassword ? "text" : "password"}
                                        name="password"
                                    />
                                    {viewPassword ? (
                                        <Eye className="cursor-pointer order-1" onClick={handleViewPass} />
                                    ) : (
                                        <EyeOff className="cursor-pointer order-1" onClick={handleViewPass} />
                                    )}
                                </div>
                                {formik.errors.password && formik.touched.password ? <div className="text-red-500">{formik.errors.password}</div> : null}
                            </div>
                            {role === "trainer" ? (
                                <div className='w-full my-5 flex justify-end'>
                                    <button type='submit' className="bg-white text-primary-500 p-2 w-full mt-5 border rounded-md border-primary-500 md:w-[158px] hover:bg-primary-500 hover:text-white hover:border-transparent transition-all duration-500">
                                        استكمال
                                    </button>
                                </div>
                            ) : (
                                <div className='w-full my-5 flex justify-end'>
                                    {loading ? (
                                        <BeatLoader className="mt-5" color="#072605" />
                                    ) : (
                                        <button type='submit' className="bg-white text-primary-500 p-2 w-full mt-5 border rounded-md border-primary-500 md:w-[158px] hover:bg-primary-500 hover:text-white hover:border-transparent transition-all duration-500">
                                            انشاء حساب
                                        </button>
                                    )}
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CommonRegister;